import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { usePersonalSetup } from '../../contexts/PersonalSetupContext'
import { useThemeColors } from '../../hooks/useThemeColors'
import { ChevronRight, ChevronLeft, X, User, Phone, Settings, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'

// Step Components
const WelcomeStep = () => {
  const { profile } = useAuth()
  const { colors } = useThemeColors()
  
  return (
    <div style={{ textAlign: 'center', maxWidth: '500px', margin: '0 auto' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👋</div>
      <h2 style={{ color: colors.text, marginBottom: '1rem' }}>
        Welcome, {profile?.full_name || 'there'}!
      </h2>
      <p style={{ color: colors.textSecondary, lineHeight: 1.6, marginBottom: '2rem' }}>
        Let's take a moment to set up your personal account. This will only take a few minutes and will help personalize your experience.
      </p>
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{
          padding: '1rem',
          backgroundColor: colors.backgroundSecondary,
          borderRadius: '8px',
          flex: '1',
          minWidth: '150px'
        }}>
          <User size={24} style={{ color: colors.primary, marginBottom: '0.5rem' }} />
          <h4 style={{ color: colors.text, margin: '0 0 0.25rem 0' }}>Profile Info</h4>
          <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: 0 }}>
            Add your contact details
          </p>
        </div>
        <div style={{
          padding: '1rem',
          backgroundColor: colors.backgroundSecondary,
          borderRadius: '8px',
          flex: '1',
          minWidth: '150px'
        }}>
          <Settings size={24} style={{ color: colors.primary, marginBottom: '0.5rem' }} />
          <h4 style={{ color: colors.text, margin: '0 0 0.25rem 0' }}>Preferences</h4>
          <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: 0 }}>
            Customize your experience
          </p>
        </div>
      </div>
    </div>
  )
}

const ProfileInfoStep = () => {
  const { profile, updateProfile } = useAuth()
  const { colors } = useThemeColors()
  const [formData, setFormData] = useState({
    phone: profile?.phone || '',
    avatar_url: profile?.avatar_url || ''
  })
  const [loading, setLoading] = useState(false)

  const handleSave = async () => {
    setLoading(true)
    try {
      const updates: any = {}
      if (formData.phone !== profile?.phone) updates.phone = formData.phone
      if (formData.avatar_url !== profile?.avatar_url) updates.avatar_url = formData.avatar_url

      if (Object.keys(updates).length > 0) {
        const result = await updateProfile(updates)
        if (result.error) {
          toast.error(result.error)
        } else {
          toast.success('Profile updated!')
        }
      }
    } catch (error) {
      toast.error('Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto' }}>
      <h2 style={{ color: colors.text, marginBottom: '0.5rem' }}>
        Your Contact Information
      </h2>
      <p style={{ color: colors.textSecondary, marginBottom: '2rem' }}>
        Help us stay in touch with you
      </p>
      
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: colors.text,
          fontSize: '0.9rem',
          marginBottom: '0.5rem'
        }}>
          <Phone size={16} />
          Phone Number
        </label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          onBlur={handleSave}
          placeholder="(555) 123-4567"
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: colors.backgroundSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            color: colors.text,
            fontSize: '1rem'
          }}
        />
        <p style={{ 
          color: colors.textSecondary, 
          fontSize: '0.8rem', 
          marginTop: '0.25rem' 
        }}>
          We'll use this to contact you about bookings and updates
        </p>
      </div>
    </div>
  )
}

const PreferencesStep = () => {
  const { colors } = useThemeColors()
  const { completeSetup } = usePersonalSetup()
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: false
  })

  const handleComplete = () => {
    // Save preferences to localStorage for now
    // In a real app, you'd save these to the database
    localStorage.setItem('user_preferences', JSON.stringify(preferences))
    completeSetup()
    toast.success('Setup complete! Welcome aboard! 🎉')
  }

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto' }}>
      <h2 style={{ color: colors.text, marginBottom: '0.5rem' }}>
        Notification Preferences
      </h2>
      <p style={{ color: colors.textSecondary, marginBottom: '2rem' }}>
        Choose how you'd like to receive updates
      </p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem',
          backgroundColor: colors.backgroundSecondary,
          borderRadius: '8px',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={preferences.emailNotifications}
            onChange={(e) => setPreferences({ ...preferences, emailNotifications: e.target.checked })}
            style={{ width: '18px', height: '18px' }}
          />
          <div>
            <div style={{ color: colors.text, fontWeight: '500' }}>Email Notifications</div>
            <div style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
              Booking confirmations, reminders, and updates
            </div>
          </div>
        </label>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem',
          backgroundColor: colors.backgroundSecondary,
          borderRadius: '8px',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={preferences.smsNotifications}
            onChange={(e) => setPreferences({ ...preferences, smsNotifications: e.target.checked })}
            style={{ width: '18px', height: '18px' }}
          />
          <div>
            <div style={{ color: colors.text, fontWeight: '500' }}>SMS Notifications</div>
            <div style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
              Text reminders for upcoming bookings
            </div>
          </div>
        </label>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem',
          backgroundColor: colors.backgroundSecondary,
          borderRadius: '8px',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={preferences.marketingEmails}
            onChange={(e) => setPreferences({ ...preferences, marketingEmails: e.target.checked })}
            style={{ width: '18px', height: '18px' }}
          />
          <div>
            <div style={{ color: colors.text, fontWeight: '500' }}>Marketing Emails</div>
            <div style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
              Special offers, new features, and facility news
            </div>
          </div>
        </label>
      </div>

      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: `${colors.primary}20`,
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <CheckCircle size={24} style={{ color: colors.primary, marginBottom: '0.5rem' }} />
        <p style={{ color: colors.text, fontSize: '0.9rem', margin: 0 }}>
          You can change these preferences anytime in your settings
        </p>
      </div>

      <button
        onClick={handleComplete}
        style={{
          width: '100%',
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: colors.primary,
          color: '#000',
          border: 'none',
          borderRadius: '8px',
          fontSize: '1rem',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        Complete Setup
      </button>
    </div>
  )
}

export default function PersonalSetupWizard() {
  const { colors } = useThemeColors()
  const { currentStep, totalSteps, skipWizard, nextStep, previousStep, shouldShowWizard } = usePersonalSetup()

  if (!shouldShowWizard) return null

  const steps = [
    { component: <WelcomeStep />, title: 'Welcome' },
    { component: <ProfileInfoStep />, title: 'Profile Info' },
    { component: <PreferencesStep />, title: 'Preferences' }
  ]

  const currentStepData = steps[currentStep]

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: colors.background,
        borderRadius: '12px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, color: colors.text }}>Personal Setup</h3>
            <p style={{ 
              margin: '0.25rem 0 0 0', 
              color: colors.textSecondary,
              fontSize: '0.9rem'
            }}>
              Step {currentStep + 1} of {totalSteps}: {currentStepData.title}
            </p>
          </div>
          <button
            onClick={skipWizard}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textSecondary,
              cursor: 'pointer',
              padding: '0.5rem'
            }}
            title="Skip setup"
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{
          height: '4px',
          backgroundColor: colors.backgroundSecondary,
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${((currentStep + 1) / totalSteps) * 100}%`,
            backgroundColor: colors.primary,
            transition: 'width 0.3s ease'
          }} />
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '2rem',
          overflowY: 'auto'
        }}>
          {currentStepData.component}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1.5rem',
          borderTop: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={previousStep}
            disabled={currentStep === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              color: currentStep === 0 ? colors.textSecondary : colors.text,
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              opacity: currentStep === 0 ? 0.5 : 1
            }}
          >
            <ChevronLeft size={18} />
            Previous
          </button>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={skipWizard}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'transparent',
                border: 'none',
                color: colors.textSecondary,
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Skip for now
            </button>
            
            {currentStep < totalSteps - 1 && (
              <button
                onClick={nextStep}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: colors.primary,
                  border: 'none',
                  borderRadius: '6px',
                  color: '#000',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                Next
                <ChevronRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}